import React, { FunctionComponent } from "react";
import { ConfigProvider as AntConfigProvider, ThemeConfig } from "antd";
import de_DE from "antd/locale/de_DE";
import en_GB from "antd/locale/en_GB";
import en_US from "antd/locale/en_US";
import es_ES from "antd/locale/es_ES";
import fr_CA from "antd/locale/fr_CA";
import fr_FR from "antd/locale/fr_FR";
import it_IT from "antd/locale/it_IT";
import nl_NL from "antd/locale/nl_NL";
import pt_BR from "antd/locale/pt_BR";
import zh_CN from "antd/locale/zh_CN";
import { AliasToken } from "antd/lib/theme/interface";
import { generate } from "@ant-design/colors";
import i18n from "i18next";
import { hexToRgb } from "@lib/hexToRgb";

interface PrimaryColorPalette {
  primary10: string;
  primary20: string;
  primary30: string;
  primary40: string;
  primary50: string;
  /**
   * The actual primary color, all values less than 60 are lighter shades, and all values greater
   * than 60 are darker shades.
   */
  primary60: string;
  primary70: string;
  primary80: string;
  primary90: string;
  primary100: string;
}

const injectCssVariables = (colors: PrimaryColorPalette) => {
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = key.replace(/([0-9]+)/, "-$1");
    root.style.setProperty(`--color-${cssVarName}`, value);
    root.style.setProperty(`--color-${cssVarName}-rgb`, hexToRgb(value));
  });
};

const primaryColors = generate(
  VisitWidget.settings.currentClientPrimaryColor,
).reduce((aggregate, current, index) => {
  aggregate[`primary${(index + 1) * 10}`] = current;
  return aggregate;
}, {} as PrimaryColorPalette);

// Inject CSS variables when primaryColors are generated
injectCssVariables(primaryColors);

const locales = {
  de: de_DE,
  "de-DE": de_DE,
  en: en_US,
  "en-GB": en_GB,
  "en-US": en_US,
  es: es_ES,
  "es-ES": es_ES,
  "es-US": es_ES,
  "es-419": es_ES,
  fr: fr_FR,
  "fr-CA": fr_CA,
  "fr-FR": fr_FR,
  it: it_IT,
  "it-IT": it_IT,
  nl: nl_NL,
  "nl-NL": nl_NL,
  pt: pt_BR,
  "pt-BR": pt_BR,
  cn: zh_CN,
  zh: zh_CN,
  "zh-CN": zh_CN,
};

const baseColors = {
  white: "#FFFFFF",
  neutral100: "#f7f9fa",
  neutral200: "#f0f1f2",
  neutral300: "#d0d4d9",
  neutral400: "#aeb1b5",
  neutral500: "#83868a",
  neutral600: "#606366",
  neutral700: "#3a3d40",
  neutral800: "#0c0d0e",
  blue100: "#f5f9ff",
  blue200: "#bfe0ff",
  blue400: "#0076e2",
  blue800: "#0059ab",
  red100: "#fff1f0",
  red200: "#ffa39e",
  red400: "#ed0915",
  red800: "#b2050e",
  red900: "#C81414",
  yellow100: "#fff7e6",
  yellow200: "#ffd591",
  yellow400: "#f2a30f",
  yellow800: "#a05f03",
  green100: "#ebf7df",
  green200: "#b7eb8f",
  green400: "#4fbd19",
  green800: "#308704",
  ...primaryColors,
};

const colors = {
  ...baseColors,
  black: baseColors.neutral800,
  // text colors
  textPrimary: baseColors.neutral800,
  textSecondary: baseColors.neutral700,
  textInvert: baseColors.white,
  textPlaceholder: baseColors.neutral600,
  textDisabled: baseColors.neutral400,
  textDanger: baseColors.red400,
  textWarning: baseColors.yellow800,
  textSuccess: baseColors.green800,
  textInformation: baseColors.blue400,
  textInformationHover: baseColors.blue800,
  // border colors
  borderPrimary: baseColors.neutral300,
  borderSecondary: baseColors.neutral400,
  borderFocused: baseColors.neutral600,
  borderDanger: baseColors.red200,
  borderWarning: baseColors.yellow200,
  borderSuccess: baseColors.green200,
  borderInformation: baseColors.blue200,
  // surface colors
  surfacePrimary: baseColors.white,
  surfaceMinimal: baseColors.neutral100,
  surfaceSecondary: baseColors.neutral200,
  surfaceDanger: baseColors.red100,
  surfaceWarning: baseColors.yellow100,
  surfaceSuccess: baseColors.green100,
  surfaceInformation: baseColors.blue100,
  // icon colors
  iconPrimary: baseColors.neutral700,
  iconSecondary: baseColors.neutral600,
  iconInvert: baseColors.white,
  iconContrast: baseColors.primary60,
  iconDisabled: baseColors.neutral500,
  iconDanger: baseColors.red400,
  iconWarning: baseColors.yellow800,
  iconSuccess: baseColors.green400,
  iconInformation: baseColors.blue400,
  // button colors
  buttonPrimary: baseColors.primary60,
  buttonPrimaryHover: baseColors.primary80,
  buttonSecondary: baseColors.neutral200,
  buttonSecondaryHover: baseColors.neutral300,
  buttonInvert: baseColors.white,
  buttonInvertHover: baseColors.neutral200,
  buttonDisabled: baseColors.neutral400,
};

const theme = {
  borderRadius: 4,
  borderRadiusLG: 6,
  borderRadiusSM: 2,
  colorBgContainerDisabled: colors.neutral200,
  colorBgTextHover: colors.neutral200,
  colorBorder: colors.neutral400,
  colorEnabled: colors.green400,
  colorError: colors.red800,
  colorLink: colors.neutral600,
  colorLinkActive: colors.neutral800,
  colorLinkHover: colors.neutral800,
  colorTextDisabled: colors.textDisabled,
  colorTextPlaceholder: colors.textPlaceholder,
  controlOutline: "#3a3d4030",
  colorPrimaryHover: colors.neutral700,
  colorBlack: colors.black,
  colorWhite: colors.white,
  colorGrayBase: colors.neutral500,
  colorGray10: colors.neutral200,
  colorGray60: colors.neutral600,
  colorGray80: colors.neutral700,
  controlHeightLG: 46,
  fontFamily: "Open Sans",
  fontSize: 14,
  fontSizeLG: 16,
} as Partial<AliasToken & { colorEnabled?: string }>;

const clientTheme = {
  ...theme,
  colorPrimary: VisitWidget.settings.currentClientPrimaryColor,
  colorPrimaryActive: VisitWidget.settings.currentClientPrimaryColor,
  colorPrimaryHover: primaryColors.primary40,
  colorLink: VisitWidget.settings.currentClientPrimaryColor,
} as Partial<AliasToken & { colorEnabled?: string }>;

const ConfigProvider: FunctionComponent<ConfigProviderProps> = ({
  children,
  type = "regular",
  primaryOverride,
  themeConfigOverride,
}) => {
  let themeToken = type === "client" ? clientTheme : theme;

  React.useEffect(() => {
    if (primaryOverride) {
      // Re-generate and inject CSS variables if primaryOverride changes
      const overridePrimaryColors = generate(primaryOverride).reduce(
        (aggregate, current, index) => {
          aggregate[`primary${(index + 1) * 10}`] = current;
          return aggregate;
        },
        {} as PrimaryColorPalette,
      );

      injectCssVariables(overridePrimaryColors);
    }
  }, [primaryOverride]);

  if (primaryOverride) {
    themeToken = {
      ...themeToken,
      colorPrimary: primaryOverride,
      colorPrimaryHover: primaryOverride,
      fontFamily: "Open Sans",
    };
  }

  return (
    <AntConfigProvider
      locale={locales[i18n.language]}
      theme={{
        token: themeToken,
        components: {
          Form: {
            itemMarginBottom: 16,
          },
        },
        ...themeConfigOverride,
      }}>
      {children}
    </AntConfigProvider>
  );
};

export interface ConfigProviderProps {
  children: React.ReactNode;
  type?: "client" | "regular";
  primaryOverride?: string;
  themeConfigOverride?: ThemeConfig;
}

export { ConfigProvider, theme, clientTheme, colors };
